import { Controller } from "@hotwired/stimulus"

// Keyboard shortcuts controller for common actions
// Usage: Add data-controller="keyboard-shortcuts" to body or main container
export default class extends Controller {
  static targets = ["saveButton", "cancelButton", "searchInput"]

  connect() {
    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.handleKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown)
  }

  handleKeydown(event) {
    // Don't trigger shortcuts when typing in inputs
    const isTyping = event.target.matches("input, textarea, select, [contenteditable]")
    
    // Ctrl/Cmd + S: Save
    if ((event.ctrlKey || event.metaKey) && event.key === "s") {
      event.preventDefault()
      this.save()
      return
    }

    // Escape: Cancel/close
    if (event.key === "Escape") {
      this.cancel()
      return
    }

    // Don't process other shortcuts while typing
    if (isTyping) return

    // Forward slash: Focus search
    if (event.key === "/") {
      event.preventDefault()
      this.focusSearch()
      return
    }

    // Ctrl/Cmd + K: Quick search (alternative)
    if ((event.ctrlKey || event.metaKey) && event.key === "k") {
      event.preventDefault()
      this.focusSearch()
      return
    }

    // N: New content (when not editing)
    if (event.key === "n" && !event.ctrlKey && !event.metaKey) {
      this.newContent()
      return
    }

    // E: Edit current content
    if (event.key === "e" && !event.ctrlKey && !event.metaKey) {
      this.editContent()
      return
    }

    // G then H: Go Home
    if (event.key === "g") {
      this.waitForSecondKey("h", () => {
        window.location.href = "/admin"
      })
      return
    }

    // G then B: Go to Books
    if (event.key === "g") {
      this.waitForSecondKey("b", () => {
        window.location.href = "/admin/books"
      })
      return
    }

    // G then C: Go to Contents
    if (event.key === "g") {
      this.waitForSecondKey("c", () => {
        window.location.href = "/admin/contents"
      })
      return
    }
  }

  save() {
    // Try to find save button
    const saveBtn = this.hasSaveButtonTarget 
      ? this.saveButtonTarget 
      : document.querySelector("input[type='submit'], button[type='submit'], .btn-primary[type='submit']")
    
    if (saveBtn) {
      saveBtn.click()
    }
  }

  cancel() {
    // Close any open modal first
    const modal = document.querySelector(".modal.show, [data-modal-open]")
    if (modal) {
      const closeBtn = modal.querySelector("[data-action*='close'], .close-button, .btn-close")
      if (closeBtn) closeBtn.click()
      return
    }

    // Otherwise, try cancel button or go back
    const cancelBtn = this.hasCancelButtonTarget
      ? this.cancelButtonTarget
      : document.querySelector("a.btn-secondary[href*='back'], a.btn-secondary:contains('Cancel')")
    
    if (cancelBtn) {
      cancelBtn.click()
    }
  }

  focusSearch() {
    const searchInput = this.hasSearchInputTarget
      ? this.searchInputTarget
      : document.querySelector(".search-input, input[type='search'], input[name='q']")
    
    if (searchInput) {
      searchInput.focus()
      searchInput.select()
    }
  }

  newContent() {
    const newLink = document.querySelector("a[href*='/new'], a.btn-primary:contains('New')")
    if (newLink) {
      newLink.click()
    }
  }

  editContent() {
    const editLink = document.querySelector("a[href*='/edit'], a.btn-primary:contains('Edit')")
    if (editLink) {
      editLink.click()
    }
  }

  waitForSecondKey(key, callback) {
    const timeout = setTimeout(() => {
      document.removeEventListener("keydown", handler)
    }, 1000)

    const handler = (event) => {
      if (event.key === key) {
        event.preventDefault()
        clearTimeout(timeout)
        document.removeEventListener("keydown", handler)
        callback()
      }
    }

    document.addEventListener("keydown", handler)
  }
}
